// Plain-language explanations of what each module/page does.
// Used by ModuleGuide to describe the modules a user actually has access to.
//
// Admin entries are keyed by the LAST segment of the menu route
// (e.g. menu.route "admin/thematic-areas" -> key "thematic-areas").
// Author entries are keyed by the AUTHOR_MENUS id.

export interface GuideEntry {
  // Short one-liner shown under the module name.
  summary: string;
  // Optional longer explanation / what you can do here.
  details?: string;
}

// Turns a full menu route ("admin/thematic-areas") into its lookup key ("thematic-areas").
export const routeKey = (route: string): string => {
  const clean = route.replace(/^\/+|\/+$/g, '');
  const parts = clean.split('/');
  return parts[parts.length - 1] || clean;
};

export const ADMIN_GUIDE: Record<string, GuideEntry> = {
  // Settings
  'setup-menu': {
    summary: 'Control which menus appear for each role.',
    details: 'Create menu items, assign them to a group, and decide which roles can see and open them across the platform.',
  },
  'event-cycle': {
    summary: 'Open and manage submission cycles.',
    details: 'Set up the periods when authors can submit and when assessments run, so activity is tied to a defined cycle.',
  },
  'thematic-areas': {
    summary: 'Manage the thematic areas submissions are grouped under.',
    details: 'Add thematic areas and activate or deactivate them. Deactivated areas stay on record but can no longer be used for new work.',
  },
  'manage-institution': {
    summary: 'Maintain the list of institutions.',
    details: 'Add and update the institutions authors and reviewers are affiliated with.',
  },
  'audit-logs': {
    summary: 'Review a trail of actions taken in the system.',
    details: 'See who did what and when, for accountability and troubleshooting.',
  },

  // Users
  'manage-users': {
    summary: 'Create and manage user accounts and their roles.',
    details: 'Edit a user\'s details, change the role assigned to them, and manage account access.',
  },
  'reviewers': {
    summary: 'View and manage reviewers.',
    details: 'See the reviewers on the platform and manage their involvement in assessments.',
  },

  // Assignments
  'reviewer-assignment': {
    summary: 'Assign submissions to reviewers.',
    details: 'Match manuscripts to the right reviewers so evaluations can begin.',
  },
  'prima-facie-assignments': {
    summary: 'Your prima facie tasks.',
    details: 'The submissions assigned to you for a first-look (prima facie) check before full review.',
  },
  'reviewer-evaluation-assignments': {
    summary: 'Your reviewer-evaluation tasks.',
    details: 'The submissions assigned to you to evaluate the quality of the review that was carried out.',
  },
  'journal-assessment-assignments': {
    summary: 'Your journal-assessment tasks.',
    details: 'The journal support applications assigned to you to assess.',
  },
  'revision-assessments': {
    summary: 'Your revision-assessment tasks.',
    details: 'The revised submissions assigned to you to assess after authors respond to feedback.',
  },

  // Submissions
  'submissions': {
    summary: 'Work through submitted manuscripts.',
    details: 'Open submissions to review their details and move them along the process.',
  },
  'submissions-desk': {
    summary: 'Submissions waiting at your desk.',
    details: 'The manuscripts routed to your desk for action.',
  },
  'all-submissions': {
    summary: 'Every submission in one place.',
    details: 'A full list of manuscript submissions across the platform for oversight and searching.',
  },
  'revised-submissions': {
    summary: 'Revised manuscripts at your desk.',
    details: 'Submissions authors have updated after feedback, waiting for the next step.',
  },
  'all-revised-submissions': {
    summary: 'Every revised submission.',
    details: 'A full list of revised manuscripts across the platform.',
  },
  'reviewed-manuscripts-desk': {
    summary: 'Reviewed manuscripts at your desk.',
    details: 'Manuscripts that have been reviewed and need your attention.',
  },
  'all-reviewed-manuscripts': {
    summary: 'Every reviewed manuscript.',
    details: 'A full list of manuscripts that have completed review.',
  },
  'reviewed-manuscripts-for-rereview': {
    summary: 'Manuscripts flagged for re-review.',
    details: 'Reviewed manuscripts that need to go through review again.',
  },
  'search-submission': {
    summary: 'Find a specific submission.',
    details: 'Search manuscripts by their details to quickly locate a submission.',
  },
  'search-reviewed-manuscript': {
    summary: 'Find a specific reviewed manuscript.',
    details: 'Search among reviewed manuscripts to locate a particular one.',
  },
  'tetfund-approval': {
    summary: 'Approve submissions for TETFund.',
    details: 'Give the go-ahead on submissions that are ready for approval.',
  },

  // Assessments
  'manuscript-reviews': {
    summary: 'The manuscript review workspace.',
    details: 'Access the manuscripts under review and their evaluations.',
  },
  'review-decision': {
    summary: 'Record decisions on reviews.',
    details: 'Capture the outcome of a review so the submission can proceed.',
  },
  'collective-assessment': {
    summary: 'Assess submissions collectively.',
    details: 'Bring reviewer input together to form a combined assessment.',
  },
  'final-assessment': {
    summary: 'Complete the final assessment.',
    details: 'Sign off on the concluding assessment for a submission.',
  },
  'prima-facie-reports': {
    summary: 'Prima facie assessment reports.',
    details: 'View the outcomes of first-look checks carried out on submissions.',
  },
  'reviewer-evaluation-reports': {
    summary: 'Reviewer-evaluation reports.',
    details: 'View the outcomes of evaluations on how reviews were carried out.',
  },
  'journal-assessment-reports': {
    summary: 'Journal-assessment reports.',
    details: 'View the outcomes of assessments on journal support applications.',
  },
  'revision-assessment-reports': {
    summary: 'Revision-assessment reports.',
    details: 'View the outcomes of assessments carried out on revised submissions.',
  },
  'journals-desk': {
    summary: 'Journal applications at your desk.',
    details: 'The journal support applications routed to your desk for assessment.',
  },

  // Communications
  'author-communications': {
    summary: 'Message authors.',
    details: 'Send and track communications directed to authors.',
  },
  'internal-communications': {
    summary: 'Communicate internally.',
    details: 'Send and track communications between staff on the platform.',
  },

  // Assessment Reports / Official Reports (approvals)
  'approval-requests': {
    summary: 'Generate and submit reports for approval.',
    details: 'Create report batches, save them as drafts, mark them ready, and submit them to be approved.',
  },
  'approvals': {
    summary: 'Approve or reject submitted reports.',
    details: 'Review reports submitted for approval, approve or reject them, export the outcome, and notify authors where applicable.',
  },

  // Payments
  'reviewer-payments': {
    summary: 'Manage reviewer payments.',
    details: 'Track and process payments due to reviewers for their work.',
  },
};

export const AUTHOR_GUIDE: Record<string, GuideEntry> = {
  'author-dashboard': {
    summary: 'Your home base.',
    details: 'A snapshot of your activity and quick access to what you need next.',
  },
  'create-manuscript': {
    summary: 'Submit a manuscript.',
    details: 'Fill in the details and upload your manuscript to put it forward for consideration.',
  },
  'submission-journal': {
    summary: 'Apply for journal support.',
    details: 'Submit an application for support towards a professional journal.',
  },
  'my-submissions': {
    summary: 'Track everything you\'ve submitted.',
    details: 'See the status of your submissions and download the files you sent.',
  },
  'library': {
    summary: 'Browse resources.',
    details: 'Access reference materials and resources available to you.',
  },
};

export const DASHBOARD_GUIDE: GuideEntry = {
  summary: 'Your overview at a glance.',
  details: 'A summary of activity relevant to your role, with quick links into the areas you work in.',
};
